import React, { MouseEvent,useCallback, useContext, useEffect, useRef } from 'react'

import cx from 'classnames'

import { A, Avatar } from '@driftt/tide-core'

import ActivityActions, { ActionType } from 'components/ActivityActions'
import { OpenedSidebar,SidebarOpenContext } from 'context/sidebarOpenContext'
import { useEnrolledCadencesData,useEnrolledSequencesData } from 'hooks/useEnrolledSequencesData'
import {
  ActivityEndUser,
  ActivityGroupByEndUser,
  CompanyData
} from 'modules/accountActivity/domain'
import { Insight } from 'modules/accountActivity/domain'
import ActivityList from 'sections/GroupedActivities/ActivityList'

import utilityStyles from '../../../styles/utility.module.css'
import styles from './styles.module.css'

interface ExpandedActivitiesProps {
  insight: Insight
  expanded: boolean
  maxContactToShow: number
  getMaxActivitiesToShow: (contactIndex: number) => number
  onClickShowMoreContacts: () => void
  onClickShowMoreActivities: (contactIndex: number) => void
  contactList: ActivityGroupByEndUser[]
  onChangeRowHeight: (height: number) => void
  companyData: CompanyData
  presentEndUserIds?: Set<number>
  onClickAccount: () => void
  showLinkToAccountDetails: boolean
}

const ExpandedActivities: React.FC<ExpandedActivitiesProps> = ({
  insight,
  expanded,
  maxContactToShow,
  getMaxActivitiesToShow,
  onClickShowMoreContacts,
  onClickShowMoreActivities,
  contactList,
  onChangeRowHeight,
  companyData,
  presentEndUserIds,
  onClickAccount,
  showLinkToAccountDetails
}) => {
  const element = useRef<any>(null)
  const prevHeight = useRef<number | null>(null)

  const handleLayoutChange = useCallback(
    (expanded: boolean) => {
      const height = expanded ? element.current.clientHeight : -24

      if (prevHeight.current !== height) {
        prevHeight.current = height
        onChangeRowHeight(height)
      }
    },
    [onChangeRowHeight]
  )

  useEffect(() => {
    handleLayoutChange(expanded)
  })

  if (!expanded) {
    return null
  }

  return (
    <div className={styles.ExpandedActivities} onClick={(e: any) => e.stopPropagation()}>
      <div ref={element}>
        {contactList
          .slice(0, maxContactToShow)
          .map((contact: ActivityGroupByEndUser, contactIndex: number) => (
            <div className={styles.contactAndActivities} key={contactIndex}>
              <ContactAndActions
                insight={insight}
                isOnline={!!presentEndUserIds?.has(contact.endUserId)}
                contact={contact}
                companyData={companyData}
                onClickAccount={onClickAccount}
              />
              <ActivityList
                className={styles.ActivityList}
                activityList={contact.activityList}
                displayName={contact.name || contact.email || 'Site visitor'}
                maxActivitiesToShow={getMaxActivitiesToShow(contactIndex)}
                onClickShowMoreActivities={() => onClickShowMoreActivities(contactIndex)}
                email={contact.email}
              />
            </div>
          ))}
        <div className={styles.footer}>
          {contactList.length > maxContactToShow && (
            <A
              className={styles.loadMore}
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                onClickShowMoreContacts()
              }}>
              Load more session activity
            </A>
          )}
          {showLinkToAccountDetails && (
            <A className={styles.viewActivity} onClick={onClickAccount}>
              View all activity from {companyData.name || companyData.domain}
            </A>
          )}
        </div>
      </div>
    </div>
  )
}

const getDisplayLocation = (endUserData: ActivityEndUser) => {
  const {
    locationCountryCode,
    locationCountry,
    locationCity,
    locationState,
    locationStateCode
  } = endUserData

  const items =
    locationCountryCode === 'US'
      ? [
          locationCity,
          locationCity ? locationStateCode || locationState : locationState || locationStateCode
        ]
      : [locationCity || locationState || locationStateCode, locationCountry]

  return items.filter(Boolean).join(', ')
}

const ContactAndActions: React.FC<{
  insight: Insight
  contact: ActivityGroupByEndUser
  isOnline: boolean
  companyData: CompanyData
  onClickAccount: () => void
}> = ({ insight, contact, isOnline, companyData, onClickAccount }) => {
  const displayName = contact.name || contact.email || 'Site visitor'
  const role = contact.role
  const endUserData = contact.endUserData || (contact.activityList[0] as any).endUserData
  const location = getDisplayLocation(endUserData)

  const { setSidebarContact, setOpenedRightSidebar } = useContext(SidebarOpenContext)

  const handleClickContact = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()

      setSidebarContact({ ...contact, id: contact.endUserId })
      setOpenedRightSidebar(OpenedSidebar.CONTACT)
      onClickAccount()
    },
    [contact, onClickAccount, setSidebarContact, setOpenedRightSidebar]
  )

  const enrolledOutreachSequences = useEnrolledSequencesData(contact.email)
  const enrolledSalesloftCadences = useEnrolledCadencesData(contact.email)

  return (
    <div className={styles.ContactAndActions}>
      <div className={styles.contact} onClick={handleClickContact}>
        <Avatar
          user={{
            ...contact,
            id: contact.endUserId,
            avatarUrl: endUserData.avatarUrl,
            type: 'END_USER'
          }}
          withPresence={true}
          presenceState={isOnline ? 'here' : 'away'}
          isDot={true}
          className={styles.userAvatar}
        />
        <span className={cx(styles.displayName, utilityStyles.semiBoldWeight)}>{displayName}</span>
        {(role || location) && <span className={styles.roleOrLocation}>{role || location}</span>}
      </div>
      <ActivityActions
        insight={insight}
        endUserData={endUserData}
        companyData={companyData}
        actionTypes={
          [
            isOnline && ActionType.CHAT,
            ActionType.LINKEDIN,
            ActionType.EMAIL,
            ActionType.OUTREACH,
            ActionType.SALESLOFT
          ].filter(Boolean) as ActionType[]
        }
        numEnrolledOutreachSequences={enrolledOutreachSequences?.length}
        numEnrolledSalesloftCadences={enrolledSalesloftCadences?.length}
      />
    </div>
  )
}

export default ExpandedActivities
